import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Users, Target, ListTodo, Calendar, Search,
  ChevronDown, ChevronRight, AlertTriangle,
} from 'lucide-react';
import { api } from '@/services/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

// ── Types ──

interface OverviewStats {
  users: { total: number; active: number; pending: number; blocked: number };
  workspaces: { total: number; active: number };
  groups: { total: number };
  goals: { total: number; by_status: Record<string, number> };
  sprints: { total: number; by_status: Record<string, number> };
  teams: { total: number };
  tasks: { total: number; by_status: Record<string, number>; by_type: Record<string, number> };
}
interface Group { id: string; name: string; description: string | null; category: string; reference_id: string | null; member_count: number; }
interface Goal { id: string; title: string; description: string | null; status: string; priority: string; due_date: string | null; group_id: string; reference_id: string | null; progress_percentage: number; total_tasks: number; completed_tasks: number; owner: { id: string; full_name: string; avatar_url: string | null } | null; }
interface Sprint { id: string; name: string; description: string | null; status: string; start_date: string; end_date: string; reference_id: string | null; group: { id: string; name: string } | null; }
interface Team { id: string; name: string; description: string | null; reference_id: string | null; member_count: number; members: Array<{ id: string; role: string; user: { id: string; full_name: string; email: string; avatar_url: string | null } }>; }
interface Task { id: string; title: string; reference_id: string | null; task_type: string; phase_status: string; calculated_status: string; priority: string; start_date: string | null; end_date: string | null; assignee: { id: string; full_name: string; avatar_url: string | null } | null; goal: { id: string; title: string } | null; team: { id: string; name: string } | null; sprint: { id: string; name: string } | null; }

// ── Badge helpers ──
function statusBadgeClass(status: string) {
  switch (status) {
    case 'completed': case 'done': return 'bg-green-100 text-green-700 border-green-200';
    case 'active': case 'in_progress': case 'live': return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'draft': case 'not_started': case 'planning': return 'bg-gray-100 text-gray-700 border-gray-200';
    case 'cancelled': case 'late': case 'completed_late': return 'bg-red-100 text-red-700 border-red-200';
    default: return 'bg-gray-100 text-gray-600';
  }
}
function priorityBadgeClass(p: string) {
  switch (p) {
    case 'critical': case 'high': return 'bg-red-100 text-red-700 border-red-200';
    case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    default: return 'bg-blue-100 text-blue-700 border-blue-200';
  }
}
function getInitials(name: string) { return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2); }
function fmtDate(d: string) { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
function fmtDateShort(d: string) { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }

// ── Component ──

export function SystemOverviewPage() {
  const [search, setSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());
  const [taskTypeFilter, setTaskTypeFilter] = useState('all');

  const { data: stats, isLoading: statsLoading } = useQuery<OverviewStats>({
    queryKey: ['admin-overview'],
    queryFn: () => api.get('/admin/system-overview').then(r => r.data.data),
  });
  const { data: groups = [] } = useQuery<Group[]>({ queryKey: ['admin-groups'], queryFn: () => api.get('/admin/groups').then(r => r.data.data) });
  const { data: goals = [] } = useQuery<Goal[]>({ queryKey: ['admin-goals'], queryFn: () => api.get('/admin/goals').then(r => r.data.data) });
  const { data: sprints = [] } = useQuery<Sprint[]>({ queryKey: ['admin-sprints'], queryFn: () => api.get('/admin/sprints').then(r => r.data.data) });
  const { data: teams = [] } = useQuery<Team[]>({ queryKey: ['admin-teams'], queryFn: () => api.get('/admin/teams').then(r => r.data.data) });
  const { data: tasks = [] } = useQuery<Task[]>({ queryKey: ['admin-tasks'], queryFn: () => api.get('/admin/tasks').then(r => r.data.data) });

  const q = search.toLowerCase();
  const filteredGroups = groups.filter(g => g.name.toLowerCase().includes(q) || g.description?.toLowerCase().includes(q));
  const filteredSprints = sprints.filter(s => s.name.toLowerCase().includes(q) || s.group?.name?.toLowerCase().includes(q));
  const filteredTeams = teams.filter(t => t.name.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q));
  const filteredTasks = tasks.filter(t => {
    const matchSearch = t.title.toLowerCase().includes(q) || t.reference_id?.toLowerCase().includes(q) || t.assignee?.full_name?.toLowerCase().includes(q);
    const matchType = taskTypeFilter === 'all' || t.task_type === taskTypeFilter;
    return matchSearch && matchType;
  });

  const goalsForGroup = (groupId: string) => goals.filter(g => g.group_id === groupId && (g.title.toLowerCase().includes(q) || g.description?.toLowerCase().includes(q) || !q));

  // Overdue tasks count
  const overdue = tasks.filter(t => t.phase_status !== 'done' && t.end_date && new Date(t.end_date) < new Date()).length;

  return (
    <div className="space-y-6">
      <PageHeader title="System Overview" description="Comprehensive view of all groups, goals, tasks, sprints, and teams" />

      {/* Stat Cards (5-grid, matching old system) */}
      <div className="grid gap-4 md:grid-cols-5">
        {statsLoading ? Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-24" />) : (
          <>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Groups</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats?.groups.total ?? 0}</div>
                <p className="text-xs text-muted-foreground">{groups.length} active</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Teams</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats?.teams.total ?? 0}</div>
                <p className="text-xs text-muted-foreground">{teams.length} active</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Sprints</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats?.sprints.total ?? 0}</div>
                <p className="text-xs text-muted-foreground">{stats?.sprints.by_status?.active ?? 0} active/live</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Goals</CardTitle>
                <Target className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats?.goals.total ?? 0}</div>
                <p className="text-xs text-muted-foreground">
                  {stats?.goals.by_status?.active ?? 0} active, {stats?.goals.by_status?.completed ?? 0} completed
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Tasks</CardTitle>
                <ListTodo className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats?.tasks.total ?? 0}</div>
                {overdue > 0 ? (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> {overdue} overdue
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">{stats?.tasks.by_status?.done ?? 0} completed</p>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search groups, teams, sprints, goals, or tasks..." className="pl-10" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="groups" className="space-y-4">
        <TabsList>
          <TabsTrigger value="groups" className="gap-2"><Target className="h-4 w-4" /> Groups & Goals <Badge variant="secondary">{groups.length}</Badge></TabsTrigger>
          <TabsTrigger value="teams" className="gap-2"><Users className="h-4 w-4" /> Teams <Badge variant="secondary">{teams.length}</Badge></TabsTrigger>
          <TabsTrigger value="sprints" className="gap-2"><Calendar className="h-4 w-4" /> Sprints <Badge variant="secondary">{sprints.length}</Badge></TabsTrigger>
          <TabsTrigger value="tasks" className="gap-2"><ListTodo className="h-4 w-4" /> Tasks <Badge variant="secondary">{tasks.length}</Badge></TabsTrigger>
        </TabsList>

        {/* ── Groups & Goals ── */}
        <TabsContent value="groups" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Groups & Goals</CardTitle>
              <CardDescription>All groups in the system with their goals. Click to expand and view goals.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {filteredGroups.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">{search ? 'No groups found matching your search' : 'No groups in the system'}</div>
              ) : filteredGroups.map(group => {
                const groupGoals = goalsForGroup(group.id);
                const isExpanded = expandedGroups.has(group.id);
                return (
                  <Card key={group.id} className="p-4">
                    <div className="flex items-center justify-between">
                      <button className="flex-1 text-left flex items-center gap-2" onClick={() => setExpandedGroups(prev => { const n = new Set(prev); n.has(group.id) ? n.delete(group.id) : n.add(group.id); return n; })}>
                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        <div>
                          <h3 className="font-semibold">{group.name}</h3>
                          {group.description && <p className="text-sm text-muted-foreground">{group.description}</p>}
                        </div>
                      </button>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{groupGoals.length} goals</Badge>
                        <Badge variant="default">Active</Badge>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="mt-4 space-y-2">
                        {groupGoals.length === 0 ? (
                          <p className="text-sm text-muted-foreground pl-6">No goals in this group</p>
                        ) : groupGoals.map(goal => (
                          <div key={goal.id} className="pl-6 p-3 bg-muted/50 rounded-md">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-sm">{goal.title}</p>
                                  {goal.reference_id && <Badge variant="outline" className="font-mono text-xs">{goal.reference_id}</Badge>}
                                  <Badge variant="outline" className={statusBadgeClass(goal.status)}>{goal.status}</Badge>
                                  <Badge variant="outline" className={priorityBadgeClass(goal.priority)}>{goal.priority}</Badge>
                                </div>
                                {goal.description && <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{goal.description}</p>}
                                {goal.owner && (
                                  <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                                    <Avatar className="h-4 w-4"><AvatarFallback className="text-[8px]">{getInitials(goal.owner.full_name)}</AvatarFallback></Avatar>
                                    {goal.owner.full_name}
                                    {goal.due_date && <span className="ml-3">Due: {fmtDate(goal.due_date)}</span>}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="mt-2">
                              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                                <span>Progress</span>
                                <span>{Math.round(goal.progress_percentage || 0)}%</span>
                              </div>
                              <Progress value={goal.progress_percentage || 0} className="h-1.5" />
                              <p className="text-xs text-muted-foreground mt-0.5">{goal.completed_tasks}/{goal.total_tasks} tasks completed</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Teams ── */}
        <TabsContent value="teams" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Teams</CardTitle>
              <CardDescription>All teams in the system. Click to expand and view members.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {filteredTeams.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">{search ? 'No teams found' : 'No teams in the system'}</div>
              ) : filteredTeams.map(team => {
                const isExpanded = expandedTeams.has(team.id);
                return (
                  <Card key={team.id} className="p-4">
                    <div className="flex items-center justify-between">
                      <button className="flex-1 text-left flex items-center gap-2" onClick={() => setExpandedTeams(prev => { const n = new Set(prev); n.has(team.id) ? n.delete(team.id) : n.add(team.id); return n; })}>
                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        <div>
                          <h3 className="font-semibold">{team.name}</h3>
                          {team.description && <p className="text-sm text-muted-foreground">{team.description}</p>}
                        </div>
                      </button>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{team.member_count} member{team.member_count !== 1 ? 's' : ''}</Badge>
                        <Badge variant="default">Active</Badge>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="space-y-2 mt-3">
                        {team.members.length === 0 ? (
                          <p className="text-sm text-muted-foreground pl-6">No members in this team</p>
                        ) : team.members.map(m => (
                          <div key={m.id} className="pl-6 p-3 bg-muted/50 rounded-md flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarFallback className="text-xs">{getInitials(m.user.full_name)}</AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium text-sm">{m.user.full_name}</p>
                                <p className="text-xs text-muted-foreground">{m.user.email}</p>
                              </div>
                            </div>
                            <Badge variant={m.role === 'owner' ? 'default' : m.role === 'leader' ? 'secondary' : 'outline'}>{m.role}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Sprints ── */}
        <TabsContent value="sprints" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Sprints</CardTitle>
              <CardDescription>All sprints in the system with their status and dates.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {filteredSprints.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">{search ? 'No sprints found' : 'No sprints in the system'}</div>
              ) : filteredSprints.map(sprint => (
                <Card key={sprint.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">{sprint.name}</h3>
                      {sprint.description && <p className="text-sm text-muted-foreground">{sprint.description}</p>}
                      <div className="flex items-center gap-4 mt-1">
                        <p className="text-xs text-muted-foreground">Group: {sprint.group?.name || 'Unknown'}</p>
                        <p className="text-xs text-muted-foreground">{fmtDateShort(sprint.start_date)} - {fmtDate(sprint.end_date)}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className={statusBadgeClass(sprint.status)}>{sprint.status}</Badge>
                  </div>
                </Card>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tasks ── */}
        <TabsContent value="tasks" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Tasks</CardTitle>
                  <CardDescription>All tasks in the system with their details.</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">Goal: {stats?.tasks.by_type?.goal ?? 0}</Badge>
                  <Badge variant="outline">Sprint: {stats?.tasks.by_type?.sprint ?? 0}</Badge>
                  <Badge variant="outline">General: {stats?.tasks.by_type?.general ?? 0}</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4 flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Filter by type:</span>
                {['all', 'goal', 'sprint', 'general'].map(t => (
                  <Button key={t} variant={taskTypeFilter === t ? 'default' : 'outline'} size="sm" onClick={() => setTaskTypeFilter(t)}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </Button>
                ))}
              </div>

              {filteredTasks.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">{search || taskTypeFilter !== 'all' ? 'No tasks found matching your criteria' : 'No tasks in the system'}</div>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Reference</TableHead>
                        <TableHead>Title</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Assignee</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead>Phase</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Due Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTasks.slice(0, 50).map(task => (
                        <TableRow key={task.id}>
                          <TableCell className="font-mono text-xs">{task.reference_id || '-'}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm truncate max-w-[200px]">{task.title}</p>
                              {task.task_type === 'goal' && task.goal && <p className="text-xs text-muted-foreground">Goal: {task.goal.title}</p>}
                              {task.task_type === 'sprint' && task.sprint && <p className="text-xs text-muted-foreground">Sprint: {task.sprint.name}</p>}
                              {task.task_type === 'general' && task.team && <p className="text-xs text-muted-foreground">Team: {task.team.name}</p>}
                            </div>
                          </TableCell>
                          <TableCell><Badge variant="outline" className="capitalize">{task.task_type}</Badge></TableCell>
                          <TableCell>
                            {task.assignee ? (
                              <div className="flex items-center gap-2">
                                <Avatar className="h-6 w-6"><AvatarFallback className="text-xs">{getInitials(task.assignee.full_name)}</AvatarFallback></Avatar>
                                <span className="text-sm truncate max-w-[100px]">{task.assignee.full_name}</span>
                              </div>
                            ) : <span className="text-muted-foreground">-</span>}
                          </TableCell>
                          <TableCell><Badge variant="outline" className={priorityBadgeClass(task.priority) + ' capitalize'}>{task.priority}</Badge></TableCell>
                          <TableCell><Badge variant="outline" className={statusBadgeClass(task.phase_status)}>{task.phase_status.replace('_', ' ')}</Badge></TableCell>
                          <TableCell><Badge variant="outline" className={statusBadgeClass(task.calculated_status)}>{task.calculated_status.replace('_', ' ')}</Badge></TableCell>
                          <TableCell className="text-sm">{task.end_date ? fmtDate(task.end_date) : '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
